import React, { useState, useEffect } from 'react';

const LEVELS = [
  { key: 'x',   label: '×', color: '#e74c3c', bg: '#fdf2f2' },
  { key: 'tri', label: '△', color: '#f39c12', bg: '#fef9e7' },
  { key: 'o',   label: '〇', color: '#2ecc71', bg: '#eafaf1' },
  { key: 'oo',  label: '◎', color: '#3498db', bg: '#ebf5fb' },
];

const getKey = (q) => `${q.s}_${q.q}`;

const QuizScreen = ({ questions, onFinish, onPause, isRandom, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [openedBlanks, setOpenedBlanks] = useState({});

  const [savedQuestions, setSavedQuestions] = useState(() => {
    const saved = localStorage.getItem('saved_questions');
    return saved ? JSON.parse(saved) : [];
  });

  const [levelMap, setLevelMap] = useState(() => {
    const saved = localStorage.getItem('level_map');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    setOpenedBlanks({});
  }, [currentIndex]);

  if (!questions || questions.length === 0) return <div>問題がありません</div>;

  const currentQ = questions[currentIndex];
  const qKey = getKey(currentQ);
  const currentLevel = levelMap[qKey] || null;

  const requiredCount = currentQ.type === 'indent'
  ? (currentQ.rows || []).reduce((acc, row) =>
      acc + (row.text.match(/【[^】]+】/g) || []).length, 0)
  : currentQ.type === 'grouped'
    ? (currentQ.categories || []).reduce((acc, cat) =>
        acc + (cat.groups || []).reduce((a, g) =>
          a + (g.rows || []).reduce((b, row) =>
            b + (row.term?.match(/【[^】]+】/g) || []).length
              + (row.desc?.match(/【[^】]+】/g) || []).length
          , 0)
        , 0)
      , 0)
    : currentQ.type === 'list'
    ? (currentQ.items || []).reduce((acc, item) =>
        acc + 1 + (item.desc.match(/【[^】]+】/g) || []).length
      , 0)
    : (currentQ.q.match(/【[^】]+】/g) || []).length;

  const allOpened = requiredCount === 0 || Object.keys(openedBlanks).length >= requiredCount;

  const isSaved = savedQuestions.some(
    q => q.q === currentQ.q && q.s === currentQ.s
  );

  const handleToggleSave = () => {
    const updated = isSaved
      ? savedQuestions.filter(q => !(q.q === currentQ.q && q.s === currentQ.s))
      : [...savedQuestions, currentQ];
    setSavedQuestions(updated);
    localStorage.setItem('saved_questions', JSON.stringify(updated));
  };

  const handleSetLevel = (levelKey) => {
    const updated = {
      ...levelMap,
      [qKey]: levelMap[qKey] === levelKey ? null : levelKey,
    };
    setLevelMap(updated);
    localStorage.setItem('level_map', JSON.stringify(updated));
  };

  const handleNext = () => {
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      alert('すべての問題が終了しました！');
      onFinish();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

const renderQuestionWithBlank = () => {
  // ★ 共通のrenderBlanks（全型で使う）
  const renderBlanks = (text, prefix) => {
    if (!text) return null;
    const parts = text.split(/【([^】]+)】/);
    return parts.map((part, j) => {
      if (j % 2 === 0) return <span key={j}>{part}</span>;
      const bKey = `${prefix}_${j}`;
      const isOpened = !!openedBlanks[bKey];
      return (
        <span key={j}
          onClick={() => setOpenedBlanks(prev => ({ ...prev, [bKey]: true }))}
          style={{
            display: 'inline-block',
            padding: '0px 4px',
            borderRadius: '5px',
            border: `1.5px solid ${isOpened ? '#E74C3C' : '#3498db'}`,
            background: isOpened ? '#fdf2f2' : '#ebf5fb',
            color: isOpened ? '#E74C3C' : 'transparent',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '1rem',
            userSelect: 'none',
            minWidth: `${part.length * 1.1}em`,
            textAlign: 'center',
            margin: '0 2px',
            verticalAlign: 'middle',
          }}>
          {isOpened ? part : '　'.repeat(part.length)}
        </span>
      );
    });
  };

  // indent型
  if (currentQ.type === 'indent') {
    return (
      <div style={{ fontSize: '1rem', lineHeight: '2.2' }}>
        <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '4px' }}>
          {renderBlanks(currentQ.q, 'ind_title')}
        </div>
        {currentQ.rows.map((row, rIndex) => (
          <div key={rIndex} style={{
            paddingLeft: row.level === 0 ? '0' : '2em',
            fontWeight: 'normal',
            color: row.level === 0 ? '#2c3e50' : '#444',
          }}>
            {renderBlanks(row.text, `ind_${rIndex}`)}
          </div>
        ))}
      </div>
    );
  }

  // grouped型
  if (currentQ.type === 'grouped') {
    return (
      <div style={{ fontSize: '0.88rem' }}>
        {currentQ.categories.map((cat, catIndex) => (
          <div key={catIndex} style={{
            marginBottom: '14px',
            borderRadius: '12px',
            border: '0.5px solid #e1e8ed',
            overflow: 'hidden',
            background: 'white',
          }}>
            <div style={{
              padding: '7px 14px',
              background: '#f8f9fa',
              fontWeight: '500',
              fontSize: '0.85rem',
              borderBottom: '0.5px solid #e1e8ed',
            }}>
              {cat.label}
            </div>
            {cat.groups.map((group, gIndex) => (
              <div key={gIndex} style={{
                borderBottom: gIndex < cat.groups.length - 1 ? '0.5px solid #e1e8ed' : 'none',
              }}>
                {group.label && (
                  <div style={{
                    padding: '5px 14px 2px',
                    fontSize: '0.75rem',
                    color: '#95a5a6',
                    fontWeight: '500',
                  }}>
                    {group.label}
                  </div>
                )}
                {group.rows.map((row, rIndex) => (
                  <div key={rIndex} style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    padding: '3px 14px 6px',
                    gap: '6px',
                    flexWrap: 'wrap',
                  }}>
                    {row.term && (
                      <>
                        <span style={{ color: '#2c3e50', flexShrink: 0 }}>
                          {renderBlanks(row.term, `g_${catIndex}_${gIndex}_${rIndex}_term`)}
                        </span>
                        <span style={{ color: '#bdc3c7', fontSize: '0.8rem' }}>：</span>
                      </>
                    )}
                    <span style={{ color: '#555', lineHeight: '1.7', flex: 1 }}>
                      {renderBlanks(row.desc, `g_${catIndex}_${gIndex}_${rIndex}_desc`)}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // list型
  if (currentQ.type === 'list') {
    return (
      <>
        <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '8px' }}>
          {renderBlanks(currentQ.q, 'list_title')}
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {currentQ.items.map((item, itemIndex) => (
            <li key={itemIndex} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontWeight: 'bold', minWidth: '1.5em' }}>{item.label}</span>
              <span
                onClick={() => setOpenedBlanks(prev => ({ ...prev, [`list_${itemIndex}_term`]: true }))}
                style={{
                  display: 'inline-block',
                  padding: '1px 8px',
                  borderRadius: '6px',
                  border: `2px solid ${openedBlanks[`list_${itemIndex}_term`] ? '#E74C3C' : '#3498db'}`,
                  background: openedBlanks[`list_${itemIndex}_term`] ? '#fdf2f2' : '#ebf5fb',
                  color: openedBlanks[`list_${itemIndex}_term`] ? '#E74C3C' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1.05rem',
                  userSelect: 'none',
                  minWidth: `${item.term.length * 1.2}em`,
                  height: '28px',
                  lineHeight: '28px',
                  textAlign: 'center',
                }}
              >
                {openedBlanks[`list_${itemIndex}_term`] ? item.term : '　'.repeat(item.term.length)}
              </span>
              <span style={{ fontWeight: 'bold', margin: '0 4px' }}>：</span>
              <span style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                {renderBlanks(item.desc, `list_${itemIndex}_desc`)}
              </span>
            </li>
          ))}
        </ul>
      </>
    );
  }

  // 通常の穴埋め型
  const parts = currentQ.q.split(/【([^】]+)】/);
  let blankIndex = 0;
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) {
          return <span key={i}>{part}</span>;
        } else {
          const thisIndex = blankIndex++;
          const isOpened = !!openedBlanks[thisIndex];
          const charCount = part.length;
          return (
            <span key={i}
              onClick={() => setOpenedBlanks(prev => ({ ...prev, [thisIndex]: true }))}
              style={{
                display: 'inline-block',
                minWidth: `${charCount * 1.1}em`,
                height: '28px',
                lineHeight: '28px',
                padding: '1px 4px',
                borderRadius: '6px',
                border: `2px solid ${isOpened ? '#E74C3C' : '#3498db'}`,
                background: isOpened ? '#fdf2f2' : '#ebf5fb',
                color: isOpened ? '#E74C3C' : 'transparent',
                cursor: 'pointer',
                textAlign: 'center',
                margin: '0 3px',
                userSelect: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                verticalAlign: 'middle',
              }}
            >
              {isOpened ? part : '　'.repeat(charCount)}
            </span>
          );
        }
      })}
    </>
  );
};

  return (
    <div style={{
      padding: '16px',
      width: '100%',
      maxWidth: '600px',
      margin: '0 auto',
      boxSizing: 'border-box',
    }}>
      {/* ヘッダー行 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <button onClick={onFinish}
          style={{ borderRadius: '10px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.85rem' }}>
          ✕ 終了
        </button>
        <span style={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
          {isRandom ? 'ランダム' : '通常'}: {currentIndex + 1} / {questions.length}
        </span>
        <button onClick={() => onPause(currentIndex)}
          style={{ borderRadius: '10px', padding: '5px 12px', background: '#f39c12', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>
          ⏸ 中断
        </button>
      </div>

      {/* 問題カード */}
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        border: '1px solid rgba(255,255,255,0.3)',
        boxShadow: '0 20px 50px rgba(149,157,165,0.1)',
        padding: '20px',
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
      }}>
        {/* タグ行 */}
        <div style={{ fontSize: '0.75rem', color: '#3498db', marginBottom: '12px' }}>
          {currentQ.ch} - {currentQ.s}
        </div>

        {/* 問題文 */}
        <div style={{ fontSize: '1.1rem', lineHeight: '2.5', marginBottom: '16px' }}>
          {renderQuestionWithBlank()}
        </div>

        {/* 全空欄オープン後：理解度ボタン */}
        {allOpened && (
          <div>
            <div style={{ fontSize: '0.72rem', color: '#95a5a6', marginBottom: '8px' }}>
              理解度を登録
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {LEVELS.map(lv => (
                <button key={lv.key}
                  onClick={() => handleSetLevel(lv.key)}
                  style={{
                    width: '52px', height: '52px',
                    borderRadius: '12px',
                    border: `2px solid ${currentLevel === lv.key ? lv.color : '#e1e8ed'}`,
                    background: currentLevel === lv.key ? lv.bg : 'white',
                    color: currentLevel === lv.key ? lv.color : '#bdc3c7',
                    fontWeight: 'bold', cursor: 'pointer',
                    fontSize: '1.2rem', transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {lv.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ボタン行 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '12px',
        width: '100%',
        gap: '6px',
      }}>
        <button onClick={handlePrev} disabled={currentIndex === 0}
          style={{
            flex: 1,
            padding: '12px 6px', borderRadius: '10px',
            border: '2px solid #e1e8ed', background: 'white',
            color: currentIndex === 0 ? '#bdc3c7' : '#2c3e50',
            fontWeight: 'bold', cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
            fontSize: '0.78rem', whiteSpace: 'nowrap',
            textAlign: 'center',
          }}>
          ← 前の問題
        </button>

        <button onClick={handleToggleSave}
          style={{
            flex: 1,
            padding: '12px 6px', borderRadius: '10px',
            border: `2px solid ${isSaved ? '#f39c12' : '#e1e8ed'}`,
            background: isSaved ? '#fef9e7' : 'white',
            color: isSaved ? '#f39c12' : '#95a5a6',
            fontWeight: 'bold', cursor: 'pointer',
            fontSize: '0.78rem', whiteSpace: 'nowrap',
            transition: 'all 0.2s', textAlign: 'center',
          }}>
          {isSaved ? '⭐ 保存済み' : '☆ 保存'}
        </button>

        <button onClick={handleNext}
          style={{
            flex: 1,
            padding: '12px 6px',
            background: '#2ecc71', color: 'white',
            border: 'none', borderRadius: '10px',
            fontWeight: 'bold', cursor: 'pointer',
            fontSize: '0.78rem', whiteSpace: 'nowrap',
            textAlign: 'center',
          }}>
          {currentIndex + 1 === questions.length ? '✅ 完了' : '次の問題 →'}
        </button>
      </div>
    </div>
  );
};

export default QuizScreen;